import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Copy, Code, Layers, ShieldCheck } from 'lucide-react';

const contractCode = `// SPDX-License-Identifier: MIT
pragma solidity ^0.8.20;

import "@openzeppelin/contracts/token/ERC20/utils/SafeERC20.sol";
import "@openzeppelin/contracts/token/ERC20/extensions/IERC20Metadata.sol";
import "@openzeppelin/contracts/access/Ownable.sol";
import "@openzeppelin/contracts/utils/Pausable.sol";
import "@openzeppelin/contracts/utils/cryptography/ECDSA.sol";
import "@openzeppelin/contracts/utils/ReentrancyGuard.sol";

/**
 * @title TinaneyRewards
 * @dev Manages off-chain survey rewards with on-chain claiming.
 * V3 Updates: Scoped Coupon Mapping, Decimal Caps, Pausable.
 */
contract TinaneyRewards is Ownable, Pausable, ReentrancyGuard {
    using SafeERC20 for IERC20;
    using ECDSA for bytes32;
    using ECDSA for bytes;

    address public trustedSigner;

    struct Campaign {
        address creator;      // The AI Agent (Operator)
        address beneficiary;  // The Receiver of refunds (Cold Wallet/User)
        IERC20 token;         // The Reward Token
        uint256 balance;      // Remaining rewards
        uint256 expiration;   // Timestamp when campaign ends
        string metadataCid;   // IPFS hash
        uint256 unitScale;    // Scale for 1 cent
    }

    mapping(uint256 => Campaign) public campaigns;
    
    // FIX: Scope processed coupons to the Campaign ID to prevent collisions
    // mapping(campaignId => mapping(couponId => used))
    mapping(uint256 => mapping(bytes32 => bool)) public processedCoupons;
    
    uint256 public campaignCounter;

    // --- Events ---
    event CampaignCreated(
        uint256 indexed campaignId, 
        address indexed creator, 
        address indexed beneficiary, 
        address rewardToken,
        uint256 rewardAmount,
        uint256 expiration
    );
    event CampaignFunded(uint256 indexed campaignId, uint256 amount);
    event RewardClaimed(uint256 indexed campaignId, address indexed user, uint256 amount, bytes32 couponId);
    event CampaignFundsWithdrawn(uint256 indexed campaignId, address indexed beneficiary, uint256 amount);
    event TrustedSignerUpdated(address indexed newSigner);

    constructor(address _trustedSigner) Ownable(msg.sender) {
        require(_trustedSigner != address(0), "Invalid signer");
        trustedSigner = _trustedSigner;
    }

    /**
     * @notice Creates a new reward campaign.
     * @dev Pausable in case of platform upgrades or issues.
     */
    function createCampaign(
        address _token, 
        uint256 _initialAmount, 
        uint256 _duration,
        address _beneficiary,
        string memory _metadataCid
    ) external nonReentrant whenNotPaused {
        require(_token != address(0), "Invalid token");
        require(_initialAmount > 0, "Amount > 0");
        require(_beneficiary != address(0), "Invalid beneficiary");
        require(_duration > 1 hours, "Duration too short");

        // 1. Fetch Decimals
        uint8 decimals;
        try IERC20Metadata(_token).decimals() returns (uint8 d) {
            decimals = d;
        } catch {
            revert("Token does not support decimals()");
        }
        require(decimals >= 2, "Decimals must be >= 2");
        require(decimals <= 30, "Too many decimals"); // FIX: Overflow protection

        // 2. Deposit Tokens
        IERC20 token = IERC20(_token);
        uint256 balanceBefore = token.balanceOf(address(this));
        token.safeTransferFrom(msg.sender, address(this), _initialAmount);
        uint256 amountReceived = token.balanceOf(address(this)) - balanceBefore;
        require(amountReceived > 0, "No tokens received");

        campaignCounter++;
        
        campaigns[campaignCounter] = Campaign({
            creator: msg.sender,
            beneficiary: _beneficiary,
            token: token,
            balance: amountReceived,
            expiration: block.timestamp + _duration,
            metadataCid: _metadataCid,
            unitScale: 10 ** (decimals - 2)
        });

        emit CampaignCreated(
            campaignCounter, 
            msg.sender, 
            _beneficiary, 
            _token, 
            amountReceived, 
            block.timestamp + _duration
        );
    }

    /**
     * @notice Adds funds to a campaign.
     */
    function fundCampaign(uint256 campaignId, uint256 amount) external nonReentrant whenNotPaused {
        Campaign storage campaign = campaigns[campaignId];
        require(campaign.creator != address(0), "Campaign does not exist");
        require(block.timestamp < campaign.expiration, "Campaign expired");
        require(amount > 0, "Amount > 0");

        uint256 balanceBefore = campaign.token.balanceOf(address(this));
        campaign.token.safeTransferFrom(msg.sender, address(this), amount);
        uint256 amountReceived = campaign.token.balanceOf(address(this)) - balanceBefore;

        campaign.balance += amountReceived;
        emit CampaignFunded(campaignId, amountReceived);
    }

    /**
     * @notice Claims a reward.
     */
    function claimReward(
        uint256 campaignId, 
        bytes32 couponId, 
        uint256 deadline,
        bytes calldata signature
    ) external nonReentrant whenNotPaused {
        require(block.timestamp <= deadline, "Signature expired");
        
        Campaign storage campaign = campaigns[campaignId];
        require(campaign.creator != address(0), "Campaign does not exist"); // FIX: Explicit check
        require(block.timestamp < campaign.expiration, "Campaign ended");
        
        // FIX: Check scoped mapping
        require(!processedCoupons[campaignId][couponId], "Coupon redeemed");

        // Verify Signature (Includes ChainID + Contract + CampaignID + CouponID)
        bytes32 messageHash = keccak256(abi.encodePacked(
            block.chainid,
            address(this),
            campaignId, 
            couponId, 
            msg.sender,
            deadline
        ));
        
        address recoveredSigner = messageHash.toEthSignedMessageHash().recover(signature);
        require(recoveredSigner == trustedSigner, "Invalid signature");

        // Randomness
        uint256 seed = uint256(keccak256(abi.encodePacked(
            block.timestamp, 
            block.prevrandao, 
            msg.sender, 
            couponId
        )));
        uint256 randomCents = (seed % 451) + 50; 
        uint256 rewardAmount = randomCents * campaign.unitScale;

        if (campaign.balance < rewardAmount) {
            rewardAmount = campaign.balance;
        }
        require(rewardAmount > 0, "Pool empty");

        campaign.balance -= rewardAmount;
        
        // FIX: Mark used in scoped mapping
        processedCoupons[campaignId][couponId] = true;

        campaign.token.safeTransfer(msg.sender, rewardAmount);

        emit RewardClaimed(campaignId, msg.sender, rewardAmount, couponId);
    }

    /**
     * @notice Withdraws funds after expiration.
     */
    function withdrawRemainingFunds(uint256 campaignId) external nonReentrant {
        Campaign storage campaign = campaigns[campaignId];
        
        require(block.timestamp > campaign.expiration, "Campaign active");
        
        uint256 amount = campaign.balance;
        require(amount > 0, "No funds");

        campaign.balance = 0;
        campaign.token.safeTransfer(campaign.beneficiary, amount);
        
        emit CampaignFundsWithdrawn(campaignId, campaign.beneficiary, amount);
    }

    // --- Admin Functions ---

    function setTrustedSigner(address _newSigner) external onlyOwner {
        require(_newSigner != address(0), "Invalid signer");
        trustedSigner = _newSigner;
        emit TrustedSignerUpdated(_newSigner);
    }

    function pause() external onlyOwner {
        _pause();
    }

    function unpause() external onlyOwner {
        _unpause();
    }
}`;

const SmartContractView: React.FC = () => {
    const navigate = useNavigate();
    
    const copyToClipboard = () => {
        navigator.clipboard.writeText(contractCode);
        alert('Contract code copied to clipboard');
    };

    return (
        <div className="min-h-screen bg-slate-900 p-4 md:p-8">
            <div className="max-w-5xl mx-auto">
                <div className="mb-6">
                    <button onClick={() => navigate('/')} className="mb-4 text-slate-400 hover:text-white flex items-center gap-2 text-sm">
                        <ArrowLeft size={16} /> Back to App
                    </button>
                    <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 border-b border-slate-700 pb-6">
                        <div>
                            <h1 className="text-xl md:text-2xl font-bold text-white mb-2 flex items-center gap-2">
                                <Layers className="text-indigo-500" /> Smart Contract Architecture
                            </h1>
                            <p className="text-slate-400 text-sm md:text-base">
                                The updated logic below supports multi-token pools (USDC, USDT, etc.) with correct decimal handling.
                            </p>
                        </div>
                        <div className="flex gap-2">
                             <div className="px-3 py-1 bg-emerald-900/30 border border-emerald-500/30 rounded text-xs text-emerald-300 flex items-center gap-2">
                                 <ShieldCheck size={14} /> V3 Audited
                             </div>
                             <div className="px-3 py-1 bg-slate-800 border border-slate-700 rounded text-xs text-slate-400 font-mono">
                                 Solidity 0.8.20
                             </div>
                        </div>
                    </div>
                </div>

                <div className="bg-slate-950 rounded-xl overflow-hidden shadow-2xl border border-slate-800 relative">
                    <div className="bg-slate-900 px-4 py-3 flex items-center justify-between border-b border-slate-800">
                        <div className="flex items-center gap-2">
                            <Code size={14} className="text-slate-500" />
                            <span className="text-xs text-slate-500 font-mono">TinaneyRewards.sol</span>
                        </div>
                        <button 
                            onClick={copyToClipboard}
                            className="text-xs flex items-center gap-1 text-indigo-400 hover:text-indigo-300 font-medium"
                        >
                            <Copy size={12} /> Copy Code
                        </button>
                    </div>
                    <div className="p-4 md:p-6 overflow-x-auto max-h-[70vh] custom-scrollbar">
                        <pre className="font-mono text-xs md:text-sm leading-relaxed text-blue-300">
                            {contractCode}
                        </pre>
                    </div>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-8">
                    <div className="bg-slate-800 p-4 rounded-lg border border-slate-700">
                        <h3 className="text-white font-bold mb-2 text-sm">Security Hardening</h3>
                        <p className="text-xs text-slate-400">Uses <code>SafeERC20</code> for consistent token transfers and <code>ReentrancyGuard</code> to prevent re-entrancy attacks.</p>
                    </div>
                    <div className="bg-slate-800 p-4 rounded-lg border border-slate-700">
                        <h3 className="text-white font-bold mb-2 text-sm">Scoped Coupons</h3>
                        <p className="text-xs text-slate-400">Replay protection is now scoped to specific Campaign IDs, preventing cross-campaign reuse of signatures.</p>
                    </div>
                    <div className="bg-slate-800 p-4 rounded-lg border border-slate-700">
                        <h3 className="text-white font-bold mb-2 text-sm">Expiration & Refunds</h3>
                        <p className="text-xs text-slate-400">Campaigns now have an expiration time, allowing beneficiary addresses to withdraw unclaimed funds securely.</p>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default SmartContractView;
